/**
 * Dates are ISO calendar dates ("2026-10-19") in the user's local time zone. The shared algorithms
 * work in epoch days (days since 1970-01-01), computed in UTC so they never shift with DST.
 */
const MS_PER_DAY = 86_400_000
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

export function isIsoDate(value: string): boolean {
  const match = ISO_DATE.exec(value)
  if (!match) return false
  const [, year, month, day] = match.map(Number) as [number, number, number, number]
  const date = new Date(Date.UTC(year, month - 1, day))
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

export function toEpochDay(isoDate: string): number {
  if (!isIsoDate(isoDate)) {
    throw new Error(`not an ISO date: ${isoDate}`)
  }
  const [year, month, day] = isoDate.split('-').map(Number) as [number, number, number]
  return Math.floor(Date.UTC(year, month - 1, day) / MS_PER_DAY)
}

export function fromEpochDay(epochDay: number): string {
  return new Date(epochDay * MS_PER_DAY).toISOString().slice(0, 10)
}

export function todayIso(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

export function addDays(isoDate: string, days: number): string {
  return fromEpochDay(toEpochDay(isoDate) + days)
}

/** Numbers group the same as on the phone, which formats with Locale.UK (UX spec §4.7). */
export const LOCALE = 'en-GB'

/**
 * Dates are spelt out here rather than by Intl, whose en-GB output varies by engine ("Sept", a comma before the
 * year); the spec's "Mon 19 Oct" and "19 Oct 2025" must read the same in every browser and on the phone.
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

/** "19 Oct": chart ticks and tight cells. Lists use formatDate, which adds the year when it is not this one. */
export function formatShortDate(isoDate: string): string {
  if (!isIsoDate(isoDate)) throw new Error(`not an ISO date: ${isoDate}`)
  const [, month, day] = isoDate.split('-').map(Number) as [number, number, number]
  return `${day} ${MONTHS[month - 1]}`
}

/** "19 Oct" in [today]'s year, "19 Oct 2025" otherwise. */
export function formatShortDateWithYear(isoDate: string, today: string): string {
  const short = formatShortDate(isoDate)
  return isoDate.slice(0, 4) === today.slice(0, 4) ? short : `${short} ${isoDate.slice(0, 4)}`
}

/** Weekday names by ISO weekday − 1 (Monday first). */
export const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const

/** ISO weekday: 1 = Monday … 7 = Sunday. Epoch day 0 (1970-01-01) was a Thursday. */
export function isoWeekday(isoDate: string): number {
  return ((((toEpochDay(isoDate) + 3) % 7) + 7) % 7) + 1
}

/** The Monday of the ISO week [isoDate] is in. */
export function startOfWeek(isoDate: string): string {
  return addDays(isoDate, 1 - isoWeekday(isoDate))
}

/** Every date from [from] to [to] inclusive; empty when [to] is before [from]. */
export function dateRange(from: string, to: string): string[] {
  const start = toEpochDay(from)
  const end = toEpochDay(to)
  const dates: string[] = []
  for (let day = start; day <= end; day++) dates.push(fromEpochDay(day))
  return dates
}

/** Whole days from [from] to [to]; negative when [to] is earlier. */
export function daysBetween(from: string, to: string): number {
  return toEpochDay(to) - toEpochDay(from)
}

/** Minute of the local day, 0–1439. */
export function nowMinute(now: Date = new Date()): number {
  return now.getHours() * 60 + now.getMinutes()
}

/** Short weekday name ("Mon") for an ISO date. */
export function formatWeekday(isoDate: string): string {
  return (WEEKDAY_NAMES[isoWeekday(isoDate) - 1] ?? '').slice(0, 3)
}

/** "Today", "Yesterday", "Tomorrow", or "Mon 19 Oct" (with the year outside [today]'s year). */
export function formatDayTitle(isoDate: string, today: string): string {
  const offset = daysBetween(today, isoDate)
  if (offset === 0) return 'Today'
  if (offset === -1) return 'Yesterday'
  if (offset === 1) return 'Tomorrow'
  return `${formatWeekday(isoDate)} ${formatShortDateWithYear(isoDate, today)}`
}
